import { Button, Flex } from "@chakra-ui/react";
import { Form, Formik, useFormikContext } from "formik";
import React from "react";
import { Card } from "src/components/Layout/Card";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useEditFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/context";
import {
  createQuestion,
  FormType,
  getQuestionTypeFormValue,
  toEmptyFormValues,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/formik";
import { useFormValidation } from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/hooks/useFormValidation";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/draft";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { QuestionVisibility } from "../../QuestionVisibility";
import { AddressQuestionForm } from "../Edit/AddressQuestion";
import { AnswersOptions } from "../Edit/AnswersOptions";
import { GradeQuestionForm } from "../Edit/GradeQuestion";
import { PermissionLevel } from "../Edit/PermissionLevel";
import {
  QuestionConstraints,
  QuestionFormat,
} from "../Edit/QuestionContraints";
import { QuestionKey } from "../Edit/QuestionKey";
import { QuestionLink } from "../Edit/QuestionLink";
import { QuestionTitle } from "../Edit/QuestionTitle";
import { QuestionType } from "../Edit/QuestionType";

import { ConfigureQuestion } from "../QuestionConfiguration/ConfigureQuestion";
import { SchoolItem } from "../types";

export type QuestionFormNewProps = {
  sectionType: AF.SectionType;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onCancelButtonClick: () => void;
  onUpdate: (question: Draft.NewQuestion) => Promise<void>;
  newQuestionId: uuid;
  gradesConfig: GQL.GetGradesConfigByOrganization_grade_config[];
  otherQuestions: readonly Draft.Question[];
};
export function QuestionFormNew({
  sectionType,
  onCancelButtonClick,
  onUpdate,
  schools,
  newQuestionId,
  gradesConfig,
  otherQuestions,
}: QuestionFormNewProps) {
  const initialValues: FormType = toEmptyFormValues(newQuestionId);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const toast = useAvelaToast();

  const { formTemplateVersion, verificationOptions: verificationOptionsRD } =
    useEditFormTemplateContext();
  const verificationOptions = React.useMemo(
    () => (verificationOptionsRD.hasData() ? verificationOptionsRD.data : []),
    [verificationOptionsRD]
  );

  const validate = useFormValidation({
    isV2FormTemplate: formTemplateVersion === "2.0.0",
    verificationOptions,
  });
  const { grades } = useEditFormTemplateContext();

  const onSubmit = React.useCallback(
    async (values: FormType) => {
      if (!grades.hasData()) {
        throw new Error("Missing grades");
      }
      setIsSubmitting(true);
      try {
        await onUpdate(
          createQuestion(
            newQuestionId,
            values,
            verificationOptions,
            grades.data
          )
        );
      } catch (err: unknown) {
        console.error(err);
        toast.error({
          title: "Oops! Something went wrong",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [newQuestionId, onUpdate, toast, verificationOptions, grades]
  );

  return (
    <Card display="flex" flexDirection="column" showBorder gap="3" padding={6}>
      <Formik<FormType>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {() => (
          <QuestionFormNewInternal
            newQuestionId={newQuestionId}
            verificationOptions={verificationOptions}
            sectionType={sectionType}
            schools={schools}
            onCancelButtonClick={onCancelButtonClick}
            isSubmitting={isSubmitting}
            gradesConfig={gradesConfig}
            otherQuestions={otherQuestions}
          />
        )}
      </Formik>
    </Card>
  );
}

type QuestionFormNewInternalProps = {
  newQuestionId: uuid;
  verificationOptions: AF.FormVerification<AF.WithId>[];
  sectionType: AF.SectionType;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onCancelButtonClick: () => void;
  isSubmitting: boolean;
  gradesConfig: GQL.GetGradesConfigByOrganization_grade_config[];
  otherQuestions: readonly Draft.Question[];
};
const QuestionFormNewInternal: React.FC<QuestionFormNewInternalProps> = ({
  newQuestionId,
  verificationOptions,
  sectionType,
  schools,
  onCancelButtonClick,
  isSubmitting,
  gradesConfig,
  otherQuestions,
}) => {
  const formik = useFormikContext<FormType>();
  const questionTypeValue = getQuestionTypeFormValue(
    newQuestionId,
    formik.values
  );

  let questionForm;
  switch (questionTypeValue) {
    case AF.GradesType:
      questionForm = (
        <GradeQuestionForm
          sectionType={sectionType}
          questionId={newQuestionId}
          gradesConfig={gradesConfig}
          otherQuestions={otherQuestions}
          schools={schools}
          verificationOptions={verificationOptions}
        />
      );
      break;

    case AF.AddressType:
      questionForm = (
        <AddressQuestionForm
          questionId={newQuestionId}
          verificationOptions={verificationOptions}
          schools={schools}
          sectionType={sectionType}
          otherQuestions={otherQuestions}
        />
      );
      break;

    default:
      questionForm = (
        <>
          <Flex direction="row" gap="4">
            <ConfigureQuestion
              questionId={newQuestionId}
              questionType={questionTypeValue}
              verificationOptions={verificationOptions}
            />
          </Flex>
          <Flex direction="column" flexGrow="1" gap="3">
            <QuestionLink questionId={newQuestionId} />
            <PermissionLevel questionId={newQuestionId} />
            <QuestionConstraints
              questionId={newQuestionId}
              questionType={questionTypeValue}
            />
            <QuestionVisibility
              sectionType={sectionType}
              questionId={newQuestionId}
              otherQuestions={otherQuestions}
              schools={schools}
            />
            <AnswersOptions
              sectionType={sectionType}
              questionId={newQuestionId}
              schools={schools}
              verificationOptions={verificationOptions}
              question={undefined}
              otherQuestions={otherQuestions}
            />
          </Flex>
        </>
      );
      break;
  }

  return (
    <Flex as={Form} direction="column" gap="6">
      <QuestionTitle questionId={newQuestionId} />
      <QuestionKey questionId={newQuestionId} otherQuestions={otherQuestions} />
      <QuestionType
        isDisabled={false}
        questionId={newQuestionId}
        sectionType={sectionType}
      />
      <QuestionFormat
        questionId={newQuestionId}
        questionType={questionTypeValue}
      />
      {questionForm}

      <Flex gap="3" justifyContent="right" paddingY="3">
        <Button
          variant="ghost"
          colorScheme="gray"
          onClick={onCancelButtonClick}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
          isDisabled={!formik.isValid}
        >
          Add question
        </Button>
      </Flex>
    </Flex>
  );
};
